import React, { useEffect, useState } from 'react';
import { createFamilyMember, deleteFamilyMember, listFamilyMembers, updateFamilyMember } from '../../service/familyMemberService';
import { getUserById, updateUser } from '../../service/user';
import { useNavigate } from "react-router-dom";

import {
    Box, Button, Card, Chip, Container, Dialog, DialogActions,
    DialogContent, DialogTitle, Grid, IconButton, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
    useMediaQuery, useTheme, Avatar, CircularProgress, FormControl, InputLabel,
    Select, MenuItem, TablePagination, TableFooter, Snackbar
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    People as PeopleIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Warning as WarningIcon
} from '@mui/icons-material';


const FamilyDetails = () => {
    const theme = useTheme();
    const userId = localStorage.getItem("user_id");
    const navigate = useNavigate();
    // State
    const [familyData, setFamilyData] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openProfileDialog, setOpenProfileDialog] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        relationship: '',
        age: '',
        gender: '',
        contact: '',
        email: '',
        medical_condition: ''
    });

    const [profileFormData, setProfileFormData] = useState({
        name: '',
        email: '',
        phone: '',
        age: '',
        gender: '',
        marital_status: '',
        address: ''
    });

    const relationshipOptions = [
        'Father', 'Mother', 'Brother', 'Sister', 'Spouse', 'Son', 'Daughter',
        'Grandfather', 'Grandmother', 'Uncle', 'Aunt', 'Cousin', 'Other'
    ];

    // Fetch user data
    const fetchUser = async () => {
        try {
            if (!userId) {
                console.error("No user_id found in localStorage");
                return;
            }
            const userData = await getUserById(userId);
            setProfileFormData(prev => ({
                ...prev,
                name: userData.name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                address: userData.address || '',
                age: userData.age ? userData.age.toString() : '',
                gender: userData.gender || '',
                marital_status: userData.marital_status || ''
            }));
        } catch (error) {
            console.error("Failed to fetch user:", error);
            showSnackbar('Failed to load user data', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch family members
    const fetchFamilyMembers = async () => {
        try {
            const data = await listFamilyMembers(userId);
            setFamilyData(data);
        } catch (err) {
            console.error("Failed to fetch family members:", err);
            showSnackbar('Failed to load family members', 'error');
        }
    };

    useEffect(() => {
        if (userId) {
            fetchUser();
            fetchFamilyMembers();
        }
    }, [userId]);

    // Handlers
    const handleAddMember = () => {
        setEditingMember(null);
        setFormData({
            name: '',
            relationship: '',
            age: '',
            gender: '',
            contact: '',
            email: '',
            medical_condition: ''
        });
        setOpenDialog(true);
    };

    const handleEditMember = (member) => {
        setEditingMember(member);
        setFormData({
            name: member.name || '',
            relationship: member.relationship || '',
            age: member.age ? member.age.toString() : '',
            gender: member.gender || '',
            contact: member.contact || '',
            email: member.email || '',
            medical_condition: member.medical_condition || ''
        });
        setOpenDialog(true);
    };

    const handleDeleteMember = async (id) => {
        if (!userId) return;
        if (!window.confirm('Are you sure you want to delete this family member?')) {
            return;
        }

        try {
            await deleteFamilyMember(id, userId);
            fetchFamilyMembers();
            showSnackbar('Family member deleted successfully', 'success');
        } catch (err) {
            console.error("Failed to delete family member:", err);
            showSnackbar("Failed to delete family member", 'error');
        }
    };

    const handleSaveProfile = async () => {
        try {
            if (!userId) return;

            if (
                profileFormData.phone &&
                profileFormData.phone.length !== 10
            ) {
                showSnackbar("Phone number must be exactly 10 digits", "error");
                return;
            }

            const payload = {
                ...profileFormData,
                age: profileFormData.age ? parseInt(profileFormData.age, 10) : null,
            };

            await updateUser(userId, payload);
            showSnackbar('Profile updated successfully', 'success');
            setOpenProfileDialog(false);
        } catch (err) {
            console.error("Failed to update profile:", err);
            const errorMsg = err?.message || err?.error || "Something went wrong while updating profile";
            showSnackbar(errorMsg, 'error');
        }
    };

    const handleSaveMember = async () => {
        try {
            if (
                formData.contact &&
                formData.contact.length !== 10
            ) {
                showSnackbar("Phone number must be exactly 10 digits", "error");
                return;
            }

            const payload = {
                ...formData,
                age: formData.age ? parseInt(formData.age, 10) : null,
            };

            if (editingMember) {
                await updateFamilyMember(editingMember.id, payload);
                showSnackbar('Family member updated successfully', 'success');
            } else {
                const createPayload = {
                    ...payload,
                    user_id: userId ? parseInt(userId, 10) : null,
                };
                await createFamilyMember(createPayload);
                showSnackbar('Family member added successfully', 'success');
            }

            fetchFamilyMembers();
            setOpenDialog(false);

            // Reset form
            setFormData({
                name: '',
                relationship: '',
                age: '',
                gender: '',
                contact: '',
                email: '',
                medical_condition: ''
            });

        } catch (err) {
            console.error("Failed to save member:", err);
            const errorMsg = err?.message || err?.error || "Something went wrong while saving family member";
            showSnackbar(errorMsg, 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Table pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', }}>
            <Container maxWidth="lg" sx={{
                py: 4,
            }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: "space-between" }}>
                        <Typography variant="h6" sx={{ fontWeight: 700,  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'  }}>
                            Family Details
                        </Typography>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                backgroundColor: '#F0F5FF',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                color: '#0B2D6B',
                                '&:hover': {
                                    backgroundColor: '#E0EBFF'
                                }
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Family Members Section */}
                <Card
                    sx={{
                        mb: 4,
                        borderRadius: 4,
                        boxShadow: 4,
                        overflow: 'hidden',
                        background: 'linear-gradient(to bottom, #f9f9f9, #ffffff)',
                    }}
                >
                    <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' , background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)'}}>
                                        <PeopleIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" component="h2">Family Members</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Manage your family's health information
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddMember}
                                    sx={{ textTransform: 'none' ,
                                         background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                    }}
                                    

                                >
                                    Add Member
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        background: 'linear-gradient(to bottom, #f4f4f4, #f9f9f9)', // very light shade
                                    }}
                                >
                                    <TableCell>Name</TableCell>
                                    <TableCell>Relationship</TableCell>
                                    <TableCell>Age</TableCell>
                                    <TableCell>Gender</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Medical Conditions</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {familyData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Box textAlign="center" py={4}>
                                                <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                                <Typography color="text.secondary" gutterBottom>
                                                    No family members added yet
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Click "Add Member" to get started
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    familyData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((member) => (
                                            <TableRow key={member.id} sx={{ bgcolor: 'white', }}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                            {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                                                        </Avatar>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {member.name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={member.relationship}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{member.age} years</TableCell>
                                                <TableCell>{member.gender}</TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">{member.contact}</Typography>
                                                        {member.email && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {member.email}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {member.medical_condition && member.medical_condition !== "None" ? (
                                                            member.medical_condition.split(",").map((condition, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={condition.trim()}
                                                                    size="small"
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                />
                                                            ))
                                                        ) : (
                                                            <Typography variant="body2" color="text.disabled">
                                                                None
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box display="flex" gap={1} justifyContent="flex-end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditMember(member)}
                                                            color="primary"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteMember(member.id)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                )}
                            </TableBody>
                            {familyData.length > 0 && (
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[5, 10, 25]}
                                            colSpan={7}
                                            count={familyData.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                        />
                                    </TableRow>
                                </TableFooter>
                            )}
                        </Table>
                    </TableContainer>
                </Card>

                {/* Add/Edit Family Member Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        {editingMember ? 'Edit Family Member' : 'Add New Family Member'}
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenDialog(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Full Name"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel>Relationship</InputLabel>
                                    <Select
                                        value={formData.relationship}
                                        label="Relationship"
                                        onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                                    >
                                        <MenuItem value="">Select relationship</MenuItem>
                                        {relationshipOptions.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Age"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={formData.age}
                                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                    inputProps={{ min: 1, max: 120 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={formData.gender}
                                        label="Gender"
                                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                    >
                                        <MenuItem value="">Select gender</MenuItem>
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Phone Number"
                                    type="tel"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={formData.contact}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "");
                                        if (value.length <= 10) {
                                            setFormData({ ...formData, contact: value });
                                        }
                                    }}
                                    error={
                                        formData.contact !== "" &&
                                        formData.contact.length !== 10
                                    }
                                    helperText={
                                        formData.contact !== "" && formData.contact.length !== 10
                                            ? "Phone number must be exactly 10 digits"
                                            : ""
                                    }
                                    inputProps={{ maxLength: 10 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    margin="normal"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="email@example.com"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Medical Conditions"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    margin="normal"
                                    value={formData.medical_condition}
                                    onChange={(e) => setFormData({ ...formData, medical_condition: e.target.value })}
                                    placeholder="Enter any medical conditions (separate multiple with commas)"
                                    helperText="Example: Diabetes, Hypertension, Asthma"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleSaveMember}
                            variant="contained"
                            disableElevation
                            sx={{background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)'}}
                        >
                            {editingMember ? 'Update' : 'Add'} Member
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Edit Profile Dialog */}
                <Dialog
                    open={openProfileDialog}
                    onClose={() => setOpenProfileDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Edit Profile</DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenProfileDialog(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Full Name"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={profileFormData.name}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={profileFormData.email}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, email: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Phone Number"
                                    type="tel"
                                    fullWidth
                                    margin="normal"
                                    required
                                    value={profileFormData.phone}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "");
                                        if (value.length <= 10) {
                                            setProfileFormData({ ...profileFormData, phone: value });
                                        }
                                    }}
                                    error={
                                        profileFormData.phone !== "" &&
                                        profileFormData.phone.length !== 10
                                    }
                                    helperText={
                                        profileFormData.phone !== "" && profileFormData.phone.length !== 10
                                            ? "Phone number must be exactly 10 digits"
                                            : ""
                                    }
                                    inputProps={{ maxLength: 10 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Age"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    value={profileFormData.age}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, age: e.target.value })}
                                    inputProps={{ min: 1, max: 120 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={profileFormData.gender}
                                        label="Gender"
                                        onChange={(e) => setProfileFormData({ ...profileFormData, gender: e.target.value })}
                                    >
                                        <MenuItem value="">Select gender</MenuItem>
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Marital Status</InputLabel>
                                    <Select
                                        value={profileFormData.marital_status}
                                        label="Marital Status"
                                        onChange={(e) => setProfileFormData({ ...profileFormData, marital_status: e.target.value })}
                                    >
                                        <MenuItem value="">Select status</MenuItem>
                                        <MenuItem value="single">Single</MenuItem>
                                        <MenuItem value="married">Married</MenuItem>
                                        <MenuItem value="divorced">Divorced</MenuItem>
                                        <MenuItem value="widowed">Widowed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Address"
                                    multiline
                                    rows={2}
                                    fullWidth
                                    margin="normal"
                                    value={profileFormData.address}
                                    onChange={(e) => setProfileFormData({ ...profileFormData, address: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenProfileDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleSaveProfile}
                            variant="contained"
                            disableElevation
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'background.paper',
                            color: snackbar.severity === 'error' ? 'error.main' : 'success.main',
                            p: 2,
                            borderRadius: 1,
                            boxShadow: 3,
                            minWidth: 300,
                            borderLeft: `4px solid ${snackbar.severity === 'error' ? theme.palette.error.main : theme.palette.success.main}`,
                        }}
                    >
                        {snackbar.severity === 'error' ? (
                            <WarningIcon sx={{ mr: 1 }} />
                        ) : (
                            <CheckIcon sx={{ mr: 1 }} />
                        )}
                        <Typography variant="body2">{snackbar.message}</Typography>
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={handleCloseSnackbar}
                            sx={{ ml: 'auto' }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Snackbar>
            </Container>
        </Box>
    );
};




export default FamilyDetails;
